
import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Edit } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import MemberBadges from '@/components/profile/MemberBadges';
import InviteFriendsCard from '@/components/profile/InviteFriendsCard';
import ChangePasswordForm from '@/components/profile/ChangePasswordForm';
import MemberReviewsTab from '@/components/profile/MemberReviewsTab';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const Profile = () => {
  const {
    user,
    userProfile
  } = useAuth();
  const navigate = useNavigate();

  // Fetch user's reviews directly
  const {
    data: userReviews = [],
    isLoading: reviewsLoading,
    refetch: refetchReviews
  } = useQuery({
    queryKey: ['user-reviews', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const {
        data,
        error
      } = await supabase.from('reviews').select('*').eq('user_id', user.id).order('created_at', {
        ascending: false
      });
      if (error) {
        console.error('Error fetching user reviews:', error);
        throw error;
      }
      return data || [];
    },
    enabled: !!user?.id
  });

  // Fetch user level based on points
  const {
    data: userLevel,
    isLoading: levelLoading
  } = useQuery({
    queryKey: ['user-level', userProfile?.points],
    queryFn: async () => {
      if (!userProfile?.points && userProfile?.points !== 0) return null;
      
      const { data, error } = await supabase
        .from('member_levels')
        .select('*')
        .lte('min_points', userProfile.points)
        .gte('max_points', userProfile.points)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user level:', error);
        return null;
      }
      
      return data;
    },
    enabled: userProfile !== undefined
  });

  if (!user) {
    return <MainLayout>
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
          <div className="container py-24 text-center px-4">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">會員中心</h1>
            <p className="text-gray-600 mb-8">請先登入以查看個人資料</p>
            <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
              <Link to="/login">登入</Link>
            </Button>
          </div>
        </div>
      </MainLayout>;
  }

  const handleEditProfile = () => {
    navigate('/profile/edit');
  };

  return <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        {/* Hero Section */}
        <section className="pt-16 sm:pt-24 pb-8 px-4">
          <div className="container max-w-6xl mx-auto">
            <div className="flex flex-col gap-4 mb-8">
              <div className="w-full">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">會員中心</h1>
                <div className="w-16 sm:w-24 h-1 bg-orange-500 mb-4"></div>
                <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-4">管理您的個人資料與投稿記錄</p>
              </div>
              <div className="w-full">
                <Button 
                  onClick={handleEditProfile} 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  編輯資料
                </Button>
              </div>
            </div>

            {/* User Profile Card */}
            <Card className="mb-8 border-l-4 border-orange-500">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col items-center gap-4 sm:gap-6">
                  <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
                    <AvatarFallback className="text-lg sm:text-xl bg-orange-100 text-orange-600">
                      {userProfile?.nickname?.charAt(0) || user.email?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 text-center w-full">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                      {userProfile?.nickname || '未設定暱稱'}
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 break-words">{user.email}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div className="bg-orange-50 rounded-lg p-3 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600 mb-1">
                          {userLevel?.name || '新手探員'}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">會員等級</div>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 mb-1">
                          {userProfile?.points || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">我的積分</div>
                      </div>
                      
                      <div className="bg-green-50 rounded-lg p-3 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 mb-1">
                          {userReviews.length}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">投稿數量</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Main Content */}
        <section className="px-4 pb-16">
          <div className="container max-w-6xl mx-auto">
            <Tabs defaultValue="reviews" className="space-y-6">
              <div className="overflow-x-auto">
                <TabsList className="grid grid-cols-4 bg-white border min-w-full">
                  <TabsTrigger value="reviews" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs sm:text-sm px-2">投稿紀錄</TabsTrigger>
                  <TabsTrigger value="badges" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs sm:text-sm px-2">勳章成就</TabsTrigger>
                  <TabsTrigger value="invite" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs sm:text-sm px-2">邀請好友</TabsTrigger>
                  <TabsTrigger value="settings" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs sm:text-sm px-2">帳號設定</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="reviews">
                <MemberReviewsTab userReviews={userReviews} isLoading={reviewsLoading} onRefresh={refetchReviews} />
              </TabsContent>

              <TabsContent value="badges">
                <MemberBadges />
              </TabsContent>

              <TabsContent value="invite">
                <InviteFriendsCard userId={user.id} />
              </TabsContent>

              <TabsContent value="settings">
                <ChangePasswordForm />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </div>
    </MainLayout>;
};

export default Profile;
